import { useCallback, useEffect, useMemo, useState } from 'react'
import { resolveFrontendApiBase } from '../utils/apiBase';
import axios from 'axios'
import { getAuth } from 'firebase/auth'

import {
  QuestionSet,
  QuestionSetsResponse,
  SurveyAnswers,
  SurveyLanguagePatchPayload,
  SurveyQuestion,
  SurveyResponse,
  SurveyResponseUpsertPayload,
} from '../types/survey'

const API_BASE = resolveFrontendApiBase()

const createEmptyAnswers = (): SurveyAnswers => ({ general: {}, languages: {} })

export interface UseLanguageSurveyOptions {
  enabled?: boolean
}

export interface UseLanguageSurveyResult {
  loading: boolean
  saving: boolean
  questions: QuestionSetsResponse | null
  response: SurveyResponse | null
  error: Error | null
  refresh: () => Promise<void>
  saveSelection: (selectedLanguages: string[]) => Promise<void>
  saveAnswers: (payload: SurveyResponseUpsertPayload) => Promise<void>
  updateLanguageAnswers: (
    language: string,
    patch: SurveyLanguagePatchPayload
  ) => Promise<void>
}

const getAuthHeaders = async () => {
  const auth = getAuth()
  const user = auth.currentUser
  if (!user) {
    throw new Error('You must be signed in to access the survey.')
  }
  const token = await user.getIdToken()
  return { Authorization: `Bearer ${token}` }
}

export const useLanguageSurvey = (
  options: UseLanguageSurveyOptions = {}
): UseLanguageSurveyResult => {
  const { enabled = true } = options

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [questions, setQuestions] = useState<QuestionSetsResponse | null>(null)
  const [response, setResponse] = useState<SurveyResponse | null>(null)
  const [error, setError] = useState<Error | null>(null)

  const fetchQuestionSets = useCallback(
    async (headers: Record<string, string>, languages: string[]): Promise<QuestionSetsResponse> => {
      const uniqueLanguages = Array.from(new Set(languages)).filter(Boolean)
      const params = uniqueLanguages.length
        ? { 'languages[]': uniqueLanguages }
        : undefined
      const { data } = await axios.get<QuestionSetsResponse>(
        `${API_BASE}/surveys/language/questions`,
        {
          headers,
          params,
        }
      )
      return data
    },
    []
  )

  const fetchAll = useCallback(async () => {
    if (!enabled) {
      return
    }
    setLoading(true)
    setError(null)
    try {
      const headers = await getAuthHeaders()
      const responseRes = await axios
        .get<SurveyResponse>(`${API_BASE}/surveys/language/responses/me`, {
          headers,
        })
        .catch((err) => {
          if (axios.isAxiosError(err) && err.response?.status === 404) {
            return null
          }
          throw err
        })

      const selectedLanguages = responseRes?.data?.selectedLanguages || []
      const questionData = await fetchQuestionSets(headers, selectedLanguages)

      setQuestions(questionData)
      setResponse(responseRes?.data || null)
    } catch (err: any) {
      console.error('Failed to load language survey data', err)
      setError(err instanceof Error ? err : new Error('Failed to load survey data'))
    } finally {
      setLoading(false)
    }
  }, [enabled, fetchQuestionSets])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const saveSelection = useCallback(
    async (selectedLanguages: string[]) => {
      setSaving(true)
      try {
        const headers = await getAuthHeaders()
        const baseAnswers = response?.answers
          ? {
              general: { ...response.answers.general },
              languages: { ...response.answers.languages },
            }
          : createEmptyAnswers()

        const selectedSet = new Set(selectedLanguages)

        // Prune answers for languages that are no longer selected
        baseAnswers.languages = Object.fromEntries(
          Object.entries(baseAnswers.languages).filter(([language]) => selectedSet.has(language))
        )

        const progress = selectedLanguages.length
          ? {
              currentLanguage: null,
              completedLanguages: {},
            }
          : undefined

        const status = !selectedLanguages.length
          ? response?.status
          : 'in_progress'

        const payload: SurveyResponseUpsertPayload = {
          selectedLanguages,
          answers: baseAnswers,
          progress,
          status,
          responseIndex: response?.responseIndex || undefined,
        }

        const { data } = await axios.post<SurveyResponse>(
          `${API_BASE}/surveys/language/responses`,
          payload,
          { headers }
        )

        setResponse(data)

        const questionData = await fetchQuestionSets(headers, selectedLanguages)
        setQuestions(questionData)
      } catch (err: any) {
        console.error('Failed to save selected languages', err)
        throw err
      } finally {
        setSaving(false)
      }
    },
    [fetchQuestionSets, response]
  )

  const saveAnswers = useCallback(
    async (payload: SurveyResponseUpsertPayload) => {
      setSaving(true)
      try {
        const headers = await getAuthHeaders()
        const { data } = await axios.post<SurveyResponse>(
          `${API_BASE}/surveys/language/responses`,
          payload,
          { headers }
        )
        setResponse(data)
      } catch (err: any) {
        console.error('Failed to save survey answers', err)
        throw err
      } finally {
        setSaving(false)
      }
    },
    []
  )

  const updateLanguageAnswers = useCallback(
    async (language: string, patch: SurveyLanguagePatchPayload) => {
      setSaving(true)
      try {
        const headers = await getAuthHeaders()
        const { data } = await axios.patch<SurveyResponse>(
          `${API_BASE}/surveys/language/responses/${encodeURIComponent(language)}`,
          patch,
          { headers }
        )
        setResponse(data)
      } catch (err: any) {
        console.error('Failed to update language answers', err)
        throw err
      } finally {
        setSaving(false)
      }
    },
    []
  )

  return useMemo(
    () => ({
      loading,
      saving,
      questions,
      response,
      error,
      refresh: fetchAll,
      saveSelection,
      saveAnswers,
      updateLanguageAnswers,
    }),
    [error, fetchAll, loading, questions, response, saveAnswers, saveSelection, saving, updateLanguageAnswers]
  )
}

export interface LanguageQuestionGroup {
  shared: SurveyQuestion[]
  specific: SurveyQuestion[]
  all: SurveyQuestion[]
}

export const mergeQuestionsForLanguage = (
  baseQuestions: QuestionSetsResponse | null,
  language: string
): LanguageQuestionGroup => {
  if (!baseQuestions) {
    return {
      shared: [],
      specific: [],
      all: [],
    }
  }

  const sharedQuestions = orderedQuestionsFromSet(baseQuestions.languageCommon)
  const languageSet = baseQuestions.languages?.[language]
  const languageSpecific = orderedQuestionsFromSet(languageSet)

  return {
    shared: sharedQuestions,
    specific: languageSpecific,
    all: [...sharedQuestions, ...languageSpecific],
  }
}

export const orderedQuestionsFromSet = (set?: QuestionSet): SurveyQuestion[] => {
  if (!set) {
    return []
  }

  const questionMap = new Map<string, SurveyQuestion>(
    (set.questions || []).map((question) => [question.questionId, question])
  )

  const ordered: SurveyQuestion[] = []
  const seen = new Set<string>()

  ;(set.order || []).forEach((id) => {
    const question = questionMap.get(id)
    if (question && question.active !== false) {
      ordered.push(question)
      seen.add(id)
    }
  })

  ;(set.questions || []).forEach((question) => {
    if (!seen.has(question.questionId) && question.active !== false) {
      ordered.push(question)
      seen.add(question.questionId)
    }
  })

  return ordered
}
